// Rendering pipeline (SPEC §7), the same for documents and comment bodies:
//   mdast → remark-rehype {allowDangerousHtml} → rehype-raw → our plugins → rehype-sanitize(STRICT_SCHEMA)
// The sanitizer is always the last step. Nothing after it produces HTML: the React layer
// only maps the sanitized tree to elements (and adds target/rel to external links).
import { unified } from 'unified';
import remarkRehype, { type Options as RemarkRehypeOptions } from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as SanitizeSchema } from 'rehype-sanitize';
import type { Root as HastRoot, Element, ElementContent } from 'hast';
import type { Root as MdRoot, RootContent, FootnoteDefinition, FootnoteReference } from 'mdast';
import type { State } from 'mdast-util-to-hast';
import GithubSlugger from 'github-slugger';
import { findAndReplace } from 'mdast-util-find-and-replace';
import { toString } from 'mdast-util-to-string';
import { parseMarkdown, walk } from './parse';

const CLOBBER = 'user-content-';

/**
 * STRICT_SCHEMA (SPEC §7): GitHub's default schema, narrowed.
 * Tags: text formatting, headings, lists (incl. task lists), quotes, code, tables,
 * details/summary, links, footnotes. No img (rendered as links), div, span, svg, math,
 * forms, iframes, styles or scripts. No style or on* attributes. Classes only from our plugins.
 */
export const STRICT_SCHEMA: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
    'em', 'strong', 'b', 'i', 'del', 's', 'br', 'hr', 'sub', 'sup', 'kbd', 'details', 'summary',
    'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a', 'input', 'section',
  ],
  attributes: {
    a: ['href', 'title', 'id'],
    code: [['className', /^language-[\w-]+$/]],
    details: ['open'],
    input: [['type', 'checkbox'], ['disabled', true], 'checked'],
    li: [['className', 'task-list-item'], 'id'],
    ol: ['start', ['className', 'contains-task-list']],
    ul: [['className', 'contains-task-list']],
    th: [['align', 'left', 'center', 'right']],
    td: [['align', 'left', 'center', 'right']],
    section: [['className', 'hq-footnote'], 'id'],
    sup: ['id'],
    // Heading anchors (GitHub-style slugs; the sanitizer adds the user-content- prefix).
    h1: ['id'], h2: ['id'], h3: ['id'], h4: ['id'], h5: ['id'], h6: ['id'],
  },
  protocols: { href: ['http', 'https', 'mailto'] },
  required: { input: { type: 'checkbox', disabled: true } },
  strip: ['script', 'style', 'template', 'textarea', 'title', 'noscript', 'iframe', 'object', 'embed', 'svg', 'math'],
  clobber: ['id', 'name'],
  clobberPrefix: CLOBBER,
  ancestors: defaultSchema.ancestors,
};

/** v1 renders images as links (SPEC §7). Runs before the sanitizer, which then checks the URL. */
function imagesAsLinks() {
  return (tree: HastRoot) => {
    const visit = (node: HastRoot | Element) => {
      node.children.forEach((child, i) => {
        if (child.type !== 'element') return;
        if (child.tagName === 'img') {
          const src = typeof child.properties.src === 'string' ? child.properties.src : '';
          const alt = typeof child.properties.alt === 'string' && child.properties.alt ? child.properties.alt : 'image';
          node.children[i] = { type: 'element', tagName: 'a', properties: { href: src }, children: [{ type: 'text', value: `Image: ${alt}` }] };
        } else {
          visit(child);
        }
      });
    };
    visit(tree);
  };
}

export type FootnoteNumbers = Map<string, number>;

/**
 * Give every heading a GitHub-style id ("## Data sources" → data-sources, duplicates get
 * -1, -2…), computed over the whole document because blocks render one at a time.
 */
export function addHeadingIds(tree: MdRoot): void {
  const slugger = new GithubSlugger();
  for (const { node } of walk(tree)) {
    if (node.type !== 'heading') continue;
    node.data = { ...node.data, hProperties: { ...(node.data?.hProperties ?? {}), id: slugger.slug(toString(node)) } };
  }
}

/** Footnote numbers in order of first reference across the whole document. */
export function footnoteNumbers(tree: MdRoot): FootnoteNumbers {
  const numbers: FootnoteNumbers = new Map();
  for (const { node } of walk(tree)) {
    if (node.type === 'footnoteReference' && !numbers.has(node.identifier)) numbers.set(node.identifier, numbers.size + 1);
  }
  return numbers;
}

const slug = (id: string) => id.toLowerCase().replace(/[^\w-]+/g, '-');

function handlers(numbers: FootnoteNumbers): NonNullable<RemarkRehypeOptions['handlers']> {
  return {
    footnoteReference(_state: State, node: FootnoteReference): Element {
      const n = numbers.get(node.identifier);
      const label = n ? String(n) : node.label ?? node.identifier;
      return { type: 'element', tagName: 'sup', properties: {}, children: [
        { type: 'element', tagName: 'a', properties: { href: `#${CLOBBER}fn-${slug(node.identifier)}` }, children: [{ type: 'text', value: label }] },
      ] };
    },
    footnoteDefinition(state: State, node: FootnoteDefinition): Element {
      const n = numbers.get(node.identifier);
      return { type: 'element', tagName: 'section', properties: { className: ['hq-footnote'], id: `fn-${slug(node.identifier)}` }, children: [
        { type: 'element', tagName: 'sup', properties: {}, children: [{ type: 'text', value: n ? String(n) : node.identifier }] },
        ...(state.all(node) as ElementContent[]),
      ] };
    },
  };
}

function pipeline(numbers: FootnoteNumbers) {
  return unified()
    .use(remarkRehype, { allowDangerousHtml: true, handlers: handlers(numbers) })
    .use(rehypeRaw)
    .use(imagesAsLinks)
    .use(rehypeSanitize, STRICT_SCHEMA); // always last
}

export type RenderedBlock =
  | { kind: 'html'; hast: HastRoot }
  | { kind: 'mermaid'; source: string };

/**
 * Render one top-level block. Mermaid code blocks are taken out before HTML rendering
 * and drawn separately (strict security level) by the UI.
 */
export function renderBlock(node: RootContent, numbers: FootnoteNumbers): RenderedBlock {
  if (node.type === 'code' && node.lang === 'mermaid') return { kind: 'mermaid', source: node.value };
  const hast = pipeline(numbers).runSync({ type: 'root', children: [node] } as MdRoot) as HastRoot;
  return { kind: 'html', hast };
}

/**
 * Link GitHub references in comment bodies: @user → profile, #123 → issue/PR.
 * Skips code, inline code and existing links. Runs on mdast, before rendering.
 */
export function linkGitHubReferences(tree: MdRoot, repoFull: string): void {
  findAndReplace(tree, [
    [/(?<![\w`/])@([a-z\d](?:[a-z\d-]{0,37}[a-z\d])?)(?![\w-])/gi, (whole: string, login: string) =>
      ({ type: 'link', url: `https://github.com/${login}`, children: [{ type: 'text', value: whole }] })],
    [/(?<![\w&/#])#(\d+)\b/g, (whole: string, n: string) =>
      ({ type: 'link', url: `https://github.com/${repoFull}/issues/${n}`, children: [{ type: 'text', value: whole }] })],
  ], { ignore: ['link', 'linkReference', 'inlineCode', 'code', 'definition'] });
}

/** Render a whole Markdown string (comment bodies). With `githubRepo`, @user and #123 become links. */
export function renderMarkdown(md: string, opts: { githubRepo?: string } = {}): HastRoot {
  const tree = parseMarkdown(md);
  if (opts.githubRepo) linkGitHubReferences(tree, opts.githubRepo);
  return pipeline(footnoteNumbers(tree)).runSync(tree) as HastRoot;
}
